import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
    createContext
} from "react";
import {
    Theme,
    StandardProps,
    CircularProgress,
    Toolbar,
    Tooltip,
    Typography,
    lighten,
    Card
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import { StyleClassKey } from "js/react/typescript";
import { useDropzone } from "react-dropzone";
import qs from "qs";
import { useS3, FileManagerHook } from "js/react/hooks/S3ContentSourceHooks";
import {
    AppButton,
    Pagination,
    PaginationProps,
    PerPageSelector,
    PerPageSelectorProps
} from "js/react/components";
import clsx from "clsx";
import {
    MediaGridView,
    DirectoryBreadCrumbsComponent,
    MediaActionsMenu,
    DirectoryItem,
    PendingUploads,
    UIMediaContent,
    toUIMediaContent,
    ShareItemInterface,
    PermissionTypeEnum
} from "js/react/scenes/components";
import CreateFolderDialog from "js/react/scenes/components/MediaManagement/CreateFolderDialog";
import RenameDirectoryDialog from "js/react/scenes/components/MediaManagement/RenameDirectoryDialog";
import {
    ContentSources,
    Contents,
    ContentSourceType,
    MimeType
} from "js/models";
import {
    Sorting,
    paginate,
    sortData,
    ColumnSort,
    addColumnSort,
    extractPaginationDetail
} from "js/types";
import Input from "@material-ui/core/Input";
import MediaListView from "js/react/scenes/components/MediaManagement/MediaListView";
import { useSnackbar } from "notistack";
import { Grid, IconButton } from "@material-ui/core";
import { Save as SaveIcon } from "@material-ui/icons";
import ViewListIcon from "@material-ui/icons/ViewList";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SearchIcon from "@material-ui/icons/Search";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStreetView } from "@fortawesome/pro-light-svg-icons";
import { ToggleButtonGroup, ToggleButton } from "@material-ui/lab";
import { useHistory, useLocation } from "react-router-dom";
import Select, { ActionMeta } from "react-select";
import { Option } from "react-select/src/filters";

const styles = (theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,

            background: theme.palette.background.paper
        },
        selected: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.grey["600"]
        },
        buttonsRow: {
            display: "flex",
            justify: "space-between"
        },
        btn: {
            width: "115px",
            padding: theme.spacing(0.5, 1.5),

            textTransform: "initial",
            fontSize: "0.85rem"
        },
        paginationContainer: {
            display: "flex",
            justifyContent: "flex-end"
        },
        pagingButtons: {
            marginLeft: theme.spacing(2)
        },
        contentRoot: {
            paddingRight: theme.spacing(2),
            display: "flex",
            minHeight: 300,
            flexDirection: "column",
            width: "100%",
            "&:focus": {
                outlineColor: "transparent !important"
            }
        },
        uploadProgress: {
            display: "flex",
            marginLeft: "-1px"
        },
        btnGroup: {
            flexGrow: 1
        },
        gridListBtn: {
            padding: "10px"
        },
        select: {
            width: "205px",
            padding: theme.spacing(0.5, 1.5),
            textTransform: "initial",
            fontSize: "0.85rem"
        }
    });

type MediaManagementClassKey = StyleClassKey<typeof styles>;

const useStyles = makeStyles(styles);

export enum ViewTypes {
    GRID = "GRID",
    LIST = "LIST"
}
export enum UploadStatus {
    COMPLETED = "done",
    FAILED = "failed",
    IN_PROGRESS = "progress"
}

export interface UploadProgress {
    fileName: string;
    size: number;
    progress: number;
    status: UploadStatus;
}

export interface FileManagerProps {
    contentSource: ContentSources;
    cloudinary: {
        accountName: string;
    };
    generatedFilesRoot: string;
    defaultShare?: string;
    selectionType: "file" | "folder";
    directoryHistory: DirectoryItem[];
    accept: string;
    filterItems?: (item: Contents) => void;
    open?: boolean;
    onOpenChanged?: (opened: boolean) => void;
    root: string; //User should never be able to access content is not child of given root
    onSelected?: (asset: Contents) => void;
    shares?: ShareItemInterface[];
}

export interface MediaManagementProps
    extends StandardProps<{}, MediaManagementClassKey>,
        FileManagerProps {}

interface EnhancedTableToolbarProps {
    onSelected: () => void;
    selection: Contents;
}

const useToolbarStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(1)
        },
        highlight:
            theme.palette.type === "light"
                ? {
                      color: theme.palette.secondary.main,
                      backgroundColor: lighten(
                          theme.palette.secondary.light,
                          0.85
                      )
                  }
                : {
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.secondary.dark
                  },
        title: {
            flex: "1 1 100%"
        }
    })
);

const getDefaultParams = () => {
    return {
        limit: 25,
        page: 1,
        search: "",
        order: [
            {
                col: "DisplayName",
                position: 0,
                dir: "asc"
            }
        ] as Sorting
    };
};

type ParamsType = ReturnType<typeof getDefaultParams>;

export const PermissionContext = createContext("");

function Component(props: MediaManagementProps) {
    const classes = useStyles(props);
    const {
        contentSource,
        root,
        generatedFilesRoot,
        onSelected,
        accept,
        shares,
        filterItems = () => true,
        directoryHistory: propDirectoryHistory
    } = props;
    const containerRef = useRef(null);
    const location = useLocation();
    const history = useHistory();
    const [view, updateView] = useState(ViewTypes.LIST);
    const [directoryHistory, setDirectoryHistory] = useState<DirectoryItem[]>(
        propDirectoryHistory
    );

    const [selectedOption, setSelectedOption] = useState<ShareItemInterface>(
        shares ? shares[0] : { generatedFilesRoot, root, permission: "" }
    );

    useEffect(() => {
        const count = directoryHistory.length - 1;
        for (let x = 0; x < count; ++x) {
            history.push({
                search: qs.stringify(getDefaultParams())
            });
        }
    }, []);
    const parent = useMemo(() => {
        if (directoryHistory.length == 0) {
            return selectedOption?.root;
        } else {
            return directoryHistory[directoryHistory.length - 1].path;
        }
    }, [directoryHistory, selectedOption]);
    const s3 = useS3(contentSource, parent, {
        generatedFilesRoot: selectedOption?.generatedFilesRoot,
        root: selectedOption?.root
    });

    const fileManagerHooksRef = useRef<FileManagerHook>();
    if (contentSource.type == ContentSourceType.S3Bucket) {
        fileManagerHooksRef.current = s3;
    }

    const queryParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true
        });

        const {
            limit = 25,
            page = 1,
            search = "",
            order = [
                {
                    col: "DisplayName",
                    position: 0,
                    dir: "asc"
                }
            ] as Sorting
        } = parsedQueryString;

        return {
            limit: +limit,
            page: +page,
            search: search ? search.toString() : "",
            order: order as Sorting
        };
    }, [location && location.search]);

    const setQueryParams = (params: Partial<ParamsType>) => {
        history.replace({
            search: qs.stringify({ ...queryParams, ...params })
        });
    };

    const [selectedIdentifiers, setSelectedIdentifiers] = useState<string[]>(
        []
    );

    useEffect(() => {
        setSelectedIdentifiers([]);
        fileManagerHooksRef.current.loadContent();
    }, [parent]);

    const [createFolderOpen, setCreateFolderOpen] = useState(false);
    const [renamedMedia, setRenamedMedia] = useState<UIMediaContent>(null);

    const [cachedItems, setCachedItems] = useState(
        fileManagerHooksRef.current.items
    );

    useEffect(() => {
        setCachedItems(fileManagerHooksRef.current.items);
    }, [fileManagerHooksRef.current.items]);

    const objectListings = useMemo((): UIMediaContent[] => {
        return cachedItems.filter(filterItems).map(toUIMediaContent);
    }, [cachedItems]);

    const selectedObjects = useMemo(
        () =>
            objectListings.filter(a =>
                selectedIdentifiers.includes(a.identifier)
            ),
        [selectedIdentifiers]
    );

    const [pendingUploads, updatePendingUploads] = useState<UploadProgress[]>(
        []
    );
    const { enqueueSnackbar } = useSnackbar();

    const insertNewObject = useCallback((obj: Contents) => {
        if (!objectListings.find(x => x.identifier == obj.identifier)) {
            setCachedItems(objectListings => [...objectListings, obj]);
            console.log(obj);
            if ([MimeType.MP4, MimeType.Webm].includes(obj.mimeType)) {
                const timeout = setTimeout(() => {
                    setCachedItems(objectListings => {
                        return objectListings.map(
                            (item): Contents => {
                                if (item.identifier === obj.identifier) {
                                    return {
                                        ...item,
                                        metadata: item.metadata
                                            ? {
                                                  ...item.metadata,
                                                  thumbnailUrl: fileManagerHooksRef.current.getThumbnailUrl(
                                                      item.identifier
                                                  )
                                              }
                                            : {
                                                  thumbnailUrl: fileManagerHooksRef.current.getThumbnailUrl(
                                                      item.identifier
                                                  )
                                              }
                                    };
                                }
                                return item;
                            }
                        );
                    });
                }, 30000);
            }
        }
        updatePendingUploads(pendingUploads =>
            pendingUploads.filter(u => u.fileName !== obj.name)
        );
    }, null);

    const createFolderInternal = async (name: string) => {
        const result = await fileManagerHooksRef.current.createFolder(
            name,
            parent
        );
        if (result == null) {
            enqueueSnackbar(`Unable to create folder "${name}".`, {
                variant: "error",
                autoHideDuration: 2000
            });
        } else {
            insertNewObject(result);
            enqueueSnackbar(`Folder ${name} created successfully.`, {
                variant: "success",
                autoHideDuration: 2000
            });
            setCreateFolderOpen(false);
        }
    };
    const uploadFile = useCallback(
        async (file: File) => {
            const result: Contents = await fileManagerHooksRef.current.upload(
                file,
                null,
                progress => {
                    updatePendingUploads(pendingUploads => {
                        const newUploads = pendingUploads.map(u => {
                            if (u.fileName === file.name) {
                                return {
                                    ...u,
                                    progress: progress
                                } as UploadProgress;
                            } else {
                                return u;
                            }
                        });
                        return newUploads;
                    });
                }
            );
            if (result !== null) {
                enqueueSnackbar(
                    'File "' + file.name + '" uploaded successfully.',
                    {
                        variant: "success",
                        autoHideDuration: 2000
                    }
                );
                updatePendingUploads(pendingUploads =>
                    pendingUploads.map(u => {
                        if (u.fileName === file.name) {
                            return {
                                ...u,
                                status: UploadStatus.COMPLETED
                            } as UploadProgress;
                        } else {
                            return u;
                        }
                    })
                );
                insertNewObject(result);
            } else {
                enqueueSnackbar(`File ${file.name} upload failed.`, {
                    variant: "error",
                    autoHideDuration: 2000
                });
                updatePendingUploads(pendingUploads =>
                    pendingUploads.map(u => {
                        if (u.fileName === file.name) {
                            return {
                                ...u,
                                status: UploadStatus.FAILED
                            } as UploadProgress;
                        } else {
                            return u;
                        }
                    })
                );
            }
        },
        [insertNewObject]
    );

    const renameDirectory = (name: string) => {
        console.log(name);
    };

    const onDrop = useCallback(
        acceptedFiles => {
            const allowedFiles = Object.values(MimeType);

            updatePendingUploads(pendingUploads => [
                ...pendingUploads,
                ...acceptedFiles
                    .filter(f => allowedFiles.includes(f.type))
                    .map(f => {
                        return {
                            fileName: f.name,
                            size: f.size,
                            progress: 0,
                            status: UploadStatus.IN_PROGRESS
                        } as UploadProgress;
                    })
            ]);
            for (let i = 0; i < acceptedFiles.length; i++) {
                if (allowedFiles.includes(acceptedFiles[i].type)) {
                    const file = acceptedFiles[i];
                    uploadFile(file);
                } else {
                    enqueueSnackbar(
                        "Only jpg, jpeg, png, mp4 and ogg files are supported.",
                        {
                            variant: "warning",
                            autoHideDuration: 2000
                        }
                    );
                }
            }
        },
        [uploadFile]
    );
    const { getRootProps, getInputProps } = useDropzone({
        onDrop
    });

    const fileInput = useRef<HTMLInputElement>(null);

    const openChooser = () => {
        if (fileInput && fileInput.current) {
            fileInput.current.click();
        }
    };

    const paginated = useMemo(() => {
        const parseQueryStringObj = queryParams;
        let filteredData = objectListings;

        // Filtering by search
        if (parseQueryStringObj.search && parseQueryStringObj.search !== "") {
            filteredData = filteredData.filter(x =>
                x.DisplayName.toLowerCase()
                    .trim()
                    .includes(parseQueryStringObj.search.toLowerCase())
            );
        }

        //Sorting on the basis of order
        if (parseQueryStringObj.order) {
            filteredData = sortData(filteredData, parseQueryStringObj.order);
        }

        // Pagination
        const pagination = paginate(filteredData, {
            current_page: parseQueryStringObj.page,
            per_page: parseQueryStringObj.limit,
            total: filteredData.length
        });

        return {
            paginationInfo: pagination.pagination_meta,
            paginatedListings: pagination.data
        };
    }, [objectListings, queryParams]);
    const updateDirectoryHistory = (history: DirectoryItem[]) => {
        setDirectoryHistory(history);
    };

    const goBack = () => {
        history.goBack();
        setDirectoryHistory(
            directoryHistory.filter(
                (x, idx) => idx !== directoryHistory.length - 1
            )
        );
    };

    if (fileManagerHooksRef.current.loading) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    height: "80vh",
                    alignItems: "center"
                }}
            >
                <CircularProgress
                    style={{
                        alignSelf: "center"
                    }}
                />
            </div>
        );
    }

    const { paginationInfo, paginatedListings } = paginated;

    const onSortChanged = (sort: ColumnSort) => {
        const currentParams = queryParams;
        const order = addColumnSort(sort, currentParams.order);
        setQueryParams({
            order,
            page: 1
        });
    };

    const paginationProps: PaginationProps = {
        onChange: page => {
            setQueryParams({
                page
            });
        },
        classes: {
            selected: classes.selected
        }
    };
    const perPageProps: PerPageSelectorProps = {
        allowed: [10, 25, 50, 100, 500],
        onChange: limit =>
            setQueryParams({
                page: 1,
                limit
            })
    };

    const onItemClick = (item: UIMediaContent) => {
        if (item.type == "folder") {
            history.push({
                search: qs.stringify(getDefaultParams())
            });
            updateDirectoryHistory([
                ...directoryHistory,
                {
                    path: item.identifier,
                    displayName: item.DisplayName
                }
            ]);
        }
    };

    const onChosen = (item: UIMediaContent) => {
        onSelected(item.detail);
    };

    const deleteFilesAndDirectories = async (id: string = null) => {
        const delItdentifiers = id
            ? [id]
            : selectedObjects.map(o => o.identifier);
        const objs = id
            ? paginated.paginatedListings
                  .map(x => x.detail)
                  .filter(x => x.identifier === id)
            : selectedObjects.map(o => o.detail);
        console.log(delItdentifiers, objs);
        if ((await fileManagerHooksRef.current.deleteItems(objs)) === null) {
            enqueueSnackbar(`Unable to delete files.`, {
                variant: "error",
                autoHideDuration: 2000
            });
        } else {
            setCachedItems(items =>
                items.filter(x => !delItdentifiers.includes(x.identifier))
            );

            setSelectedIdentifiers([]);
            enqueueSnackbar(`${objs.length} file(s) deleted successfully.`, {
                variant: "success",
                autoHideDuration: 2000
            });
        }
    };
    return (
        <PermissionContext.Provider value={selectedOption?.permission}>
            <div ref={containerRef}>
                <DirectoryBreadCrumbsComponent
                    listings={directoryHistory}
                    onUpdate={goBack}
                    view={view}
                    onViewUpdate={(view: ViewTypes) => updateView(view)}
                />

                <br />
                <div className={classes.root}>
                    <div className={classes.contentRoot} {...getRootProps()}>
                        <Grid
                            container
                            spacing={2}
                            className={classes.buttonsRow}
                        >
                            {(!selectedOption.permission ||
                                selectedOption.permission ===
                                    PermissionTypeEnum.WRITE) && (
                                <React.Fragment>
                                    <Grid item>
                                        <AppButton
                                            className={classes.btn}
                                            buttonVariant="dark"
                                            onClick={() => {
                                                openChooser();
                                            }}
                                        >
                                            Upload
                                        </AppButton>
                                    </Grid>
                                    <Grid item>
                                        <AppButton
                                            className={classes.btn}
                                            buttonVariant="dark"
                                            onClick={() => {
                                                setCreateFolderOpen(true);
                                            }}
                                        >
                                            Create folder
                                        </AppButton>
                                    </Grid>
                                </React.Fragment>
                            )}
                            {shares && (
                                <Grid item>
                                    {shares.length > 1 && (
                                        <Select
                                            className={classes.select}
                                            value={{
                                                label: selectedOption?.name,
                                                value:
                                                    selectedOption?.identifier
                                            }}
                                            onChange={(e: Option | null) => {
                                                setSelectedOption(
                                                    shares.find(
                                                        share =>
                                                            share?.identifier ===
                                                            e.value
                                                    )
                                                );
                                            }}
                                            options={shares.map(share => ({
                                                value: share.identifier,
                                                label: share.name
                                            }))}
                                        />
                                    )}
                                </Grid>
                            )}

                            <Grid item>
                                {(!selectedOption.permission ||
                                    selectedOption.permission ===
                                        PermissionTypeEnum.WRITE) &&
                                    selectedObjects.length > 0 && (
                                        <MediaActionsMenu
                                            onDelete={() => {
                                                deleteFilesAndDirectories();
                                            }}
                                        />
                                    )}
                            </Grid>
                            <Grid item className={classes.btnGroup}>
                                <Input
                                    placeholder="Search..."
                                    value={queryParams.search}
                                    onChange={e =>
                                        setQueryParams({
                                            ...queryParams,
                                            search: e.target.value
                                        })
                                    }
                                    inputProps={{ "aria-label": "description" }}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    }
                                />
                            </Grid>
                            <Grid item>
                                <ToggleButtonGroup
                                    exclusive
                                    value={view}
                                    onChange={() => {
                                        view === ViewTypes.GRID
                                            ? updateView(ViewTypes.LIST)
                                            : updateView(ViewTypes.GRID);
                                    }}
                                >
                                    <ToggleButton
                                        value={ViewTypes.GRID}
                                        aria-label="module"
                                    >
                                        <ViewModuleIcon />
                                    </ToggleButton>
                                    <ToggleButton
                                        value={ViewTypes.LIST}
                                        aria-label="list"
                                    >
                                        <ViewListIcon />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                        </Grid>
                        <br />
                        {view === ViewTypes.GRID && (
                            <MediaGridView
                                onDelete={item =>
                                    deleteFilesAndDirectories(item.identifier)
                                }
                                showPrevious={directoryHistory.length > 0}
                                onPrevious={goBack}
                                onChosen={onChosen}
                                checkedValues={selectedIdentifiers}
                                onItemClick={onItemClick}
                                onRename={setRenamedMedia}
                                selectionType={props.selectionType}
                                onCheckedValuesChange={setSelectedIdentifiers}
                                objectListings={paginatedListings}
                            />
                        )}
                        {view === ViewTypes.LIST && (
                            <MediaListView
                                selectionType={props.selectionType}
                                onDelete={item =>
                                    deleteFilesAndDirectories(item.identifier)
                                }
                                showPrevious={directoryHistory.length > 0}
                                onPrevious={goBack}
                                onChosen={onChosen}
                                checkedValues={selectedIdentifiers}
                                onItemClick={onItemClick}
                                onRename={setRenamedMedia}
                                onCheckedValuesChange={setSelectedIdentifiers}
                                objectListings={paginatedListings}
                                sortOrder={queryParams.order}
                                onSortChanged={onSortChanged}
                            />
                        )}
                        <br />
                        <div className={classes.paginationContainer}>
                            {paginationInfo &&
                                extractPaginationDetail(paginationInfo) && (
                                    <PerPageSelector
                                        selected={
                                            extractPaginationDetail(
                                                paginationInfo
                                            ).per_page
                                        }
                                        {...perPageProps}
                                    />
                                )}
                            {paginationInfo && paginationInfo.total > 0 && (
                                <div className={classes.pagingButtons}>
                                    <Pagination
                                        info={paginationInfo}
                                        {...paginationProps}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <input
                    multiple
                    {...getInputProps({
                        style: { display: "none" }
                    })}
                    accept={accept}
                    ref={fileInput}
                />
                <div className={classes.uploadProgress}>
                    {pendingUploads.length > 0 && (
                        <PendingUploads
                            width={
                                containerRef.current &&
                                containerRef.current.offsetWidth
                            }
                            uploadsList={pendingUploads}
                            onUpdate={(list: UploadProgress[]) => {
                                updatePendingUploads(list);
                            }}
                        />
                    )}
                </div>
                {createFolderOpen && (
                    <CreateFolderDialog
                        open={createFolderOpen}
                        onCreate={a => createFolderInternal(a)}
                        onClose={() => setCreateFolderOpen(false)}
                        validityState={
                            fileManagerHooksRef.current.validityState
                        }
                        loading={fileManagerHooksRef.current.loading}
                    />
                )}

                {renamedMedia && (
                    <RenameDirectoryDialog
                        open
                        name={renamedMedia.DisplayName}
                        onClose={() => setRenamedMedia(null)}
                        onRename={renameDirectory}
                        validityState={
                            fileManagerHooksRef.current.validityState
                        }
                        loading={fileManagerHooksRef.current.loading}
                    />
                )}
            </div>
        </PermissionContext.Provider>
    );
}

Component.displayName = "MediaManagement";
export const MediaManagement = Component;
export default MediaManagement;
